import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { deptApi, F106 } from '../../../services/deptApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useDeptForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F106[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [unitType, setUnitType] = useState(1);
  const [mgrCode, setMgrCode] = useState('');
  const [mgrName, setMgrName] = useState('');
  const [vpCode, setVpCode] = useState('');
  const [vpName, setVpName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [isOld, setIsOld] = useState(false);
  const [level, setLevel] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await deptApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: F106) => {
    setCode(rec.f106_01);
    setName(rec.f106_11);
    setUnitType(rec.f106_02);
    setMgrCode(rec.f106_12);
    setMgrName(rec.f106_12a);
    setVpCode(rec.f106_17);
    setVpName(rec.f106_17a);
    setShortCode(rec.f106_13);
    setIsOld(rec.f106_16);
    setLevel(rec.f106_24);
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') syncFields(current);
  }, [current, mode, syncFields]);

  useEffect(() => {
    loadRecords().then(data => { if (data.length > 0) setCurrentIdx(0); });
  }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add');
    setCode(''); setName(''); setUnitType(1); setMgrCode(''); setMgrName('');
    setVpCode(''); setVpName(''); setShortCode(''); setIsOld(false); setLevel('');
  };

  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFields(current);
  };

  const cancel = () => {
    setMode('browse');
    if (current) syncFields(current);
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await deptApi.create({
          f106_01: code, f106_02: unitType, f106_11: name, f106_12: mgrCode,
          f106_13: shortCode, f106_16: isOld, f106_17: vpCode, f106_24: level,
        });
        message.success('新增成功');
      } else {
        await deptApi.update(current!.f106_01, {
          f106_02: unitType, f106_11: name, f106_12: mgrCode,
          f106_13: shortCode, f106_16: isOld, f106_17: vpCode, f106_24: level,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f106_01;
      const idx = data.findIndex(r => r.f106_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await deptApi.delete(current.f106_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f106_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  // BR-C01/C03: Lookup employee name on code confirm (blur or selection)
  const lookupManager = async (code?: string) => {
    const c = (code ?? mgrCode).trim();
    if (!c) { setMgrName(''); return; }
    try {
      const res = await deptApi.lookupEmployee(c);
      setMgrName(res.name);
    } catch { setMgrName('?'); }
  };

  const lookupVP = async (code?: string) => {
    const c = (code ?? vpCode).trim();
    if (!c) { setVpName(''); return; }
    try {
      const res = await deptApi.lookupEmployee(c);
      setVpName(res.name);
    } catch { setVpName('?'); }
  };

  return {
    mode, isEditing, loading, records, current, currentIdx,
    code, setCode, name, setName, unitType, setUnitType,
    mgrCode, setMgrCode, mgrName, vpCode, setVpCode, vpName,
    shortCode, setShortCode, isOld, setIsOld, level, setLevel,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
    lookupManager, lookupVP,
  };
}
